import { ScreenerSettingName } from '@/hooks/screener-settings';
import { isProductTourEnabledForFacility } from '@/lib/productTourGating';
import { buildProductTourFacilityMetadata } from '@/lib/productTourMetadata';
import { UserModel } from '@/models/UserModel';

import { Loadable } from './Loadable';

export class Userpilot extends Loadable {
  public static identify(user: UserModel): void {
    if (!user.facility_id || !Userpilot.isEnabled(user.facility_id)) {
      return;
    }

    Userpilot.identifyWhenSdkReady(
      user.id,
      Userpilot.buildIdentifyOptions(user)
    );
  }

  public load(): void {
    Userpilot.ensureScriptLoaded();
  }

  private static identifyWhenSdkReady(
    userId: string,
    options: Record<string, unknown>
  ): void {
    Userpilot.ensureScriptLoaded();

    if (window.userpilot) {
      window.userpilot.identify(userId, options);
      return;
    }

    const scriptElement = document.querySelector<HTMLScriptElement>(
      'script[src="https://js.userpilot.io/sdk/latest.js"]'
    );

    scriptElement?.addEventListener(
      'load',
      () => window.userpilot?.identify(userId, options),
      { once: true }
    );
  }

  private static ensureScriptLoaded(): void {
    const appToken = import.meta.env.VITE_USERPILOT_APP_TOKEN;
    if (!appToken) {
      console.warn(
        'Userpilot app token not configured. Skipping initialization.'
      );
      return;
    }

    window.userpilotSettings = { token: appToken };

    if (
      document.querySelector(
        'script[src="https://js.userpilot.io/sdk/latest.js"]'
      )
    ) {
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.async = true;
    scriptElement.src = 'https://js.userpilot.io/sdk/latest.js';
    document.head.appendChild(scriptElement);
  }

  private static buildIdentifyOptions(user: UserModel) {
    return {
      name: user.fullName,
      email: user.email,
      user_role: user.user_type?.title,
      user_title: user.title,
      ...buildProductTourFacilityMetadata(user),
      company: user.organization_details
        ? {
            id: user.organization_details.id,
            name: user.organization_details.name
          }
        : undefined
    };
  }

  private static isEnabled(facilityId: string): boolean {
    if (!import.meta.env.VITE_USERPILOT_APP_TOKEN) {
      return false;
    }

    return isProductTourEnabledForFacility(
      ScreenerSettingName.UserpilotProductTours,
      facilityId
    );
  }
}
